/* component-quantity-input.js — <quantity-input>
   +/- stepper wrapping a native <input type=number>.
   - Enforces min (and max when present); disables minus at min.
   - On change: publishes kk:quantity:changed { key, quantity }.
   - When data-key is present (cart line item): drives KK.cart.change so the
     line item quantity syncs to the cart (drawer + cart page both reuse this).
   - When there is no data-key (e.g. PDP add-to-cart qty), it only updates the
     input value + publishes the event; the surrounding form reads the value.
   ES module, one component per file, no global pollution beyond window.KK. */

class QuantityInput extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('.kk-qty__input');
    this.minusBtn = this.querySelector('[data-qty-minus]');
    this.plusBtn = this.querySelector('[data-qty-plus]');
    if (!this.input) return;

    this.min = parseInt(this.getAttribute('data-min'), 10);
    if (isNaN(this.min)) this.min = parseInt(this.input.min, 10) || 1;
    this.key = this.getAttribute('data-key') || null;

    this._onMinus = () => this._step(-1);
    this._onPlus = () => this._step(1);
    this._onChange = () => this._commit();

    if (this.minusBtn) this.minusBtn.addEventListener('click', this._onMinus);
    if (this.plusBtn) this.plusBtn.addEventListener('click', this._onPlus);
    this.input.addEventListener('change', this._onChange);

    this._reflectDisabled();
  }

  disconnectedCallback() {
    if (this.minusBtn) this.minusBtn.removeEventListener('click', this._onMinus);
    if (this.plusBtn) this.plusBtn.removeEventListener('click', this._onPlus);
    if (this.input) this.input.removeEventListener('change', this._onChange);
  }

  get value() { return parseInt(this.input.value, 10) || this.min; }

  _max() {
    const m = parseInt(this.input.max, 10);
    return isNaN(m) ? Infinity : m;
  }

  _step(delta) {
    let next = this.value + delta;
    if (next < this.min) next = this.min;
    const max = this._max();
    if (next > max) next = max;
    if (next === this.value) { this._reflectDisabled(); return; }
    this.input.value = next;
    this._commit();
  }

  _reflectDisabled() {
    if (this.minusBtn) this.minusBtn.disabled = this.value <= this.min;
    if (this.plusBtn) this.plusBtn.disabled = this.value >= this._max();
  }

  _commit() {
    let v = this.value;
    if (v < this.min) { v = this.min; this.input.value = v; }
    const max = this._max();
    if (v > max) { v = max; this.input.value = v; }
    this._reflectDisabled();

    if (window.KK && window.KK.pubsub) {
      window.KK.pubsub.publish('kk:quantity:changed', { key: this.key, quantity: v });
    }

    // Cart line item: sync to cart via the CartStore. It publishes kk:cart:updated,
    // which re-renders the drawer / cart page. Guard against double-fire while pending.
    if (this.key && window.KK && window.KK.cart && !this._pending) {
      this._pending = true;
      this._setBusy(true);
      window.KK.cart.change(this.key, v)
        .catch((err) => {
          // revert on failure so the UI reflects the real cart
          if (window.KK.cart.state) {
            const line = window.KK.cart.state.items.find((it) => it.key === this.key);
            if (line) this.input.value = line.quantity;
          }
          this.dispatchEvent(new CustomEvent('kk:qty:error', { bubbles: true, detail: { error: err } }));
        })
        .finally(() => { this._pending = false; this._setBusy(false); this._reflectDisabled(); });
    }
  }

  _setBusy(state) {
    this.toggleAttribute('aria-busy', state);
    if (this.minusBtn) this.minusBtn.disabled = state || this.value <= this.min;
    if (this.plusBtn) this.plusBtn.disabled = state || this.value >= this._max();
  }
}

if (!customElements.get('quantity-input')) {
  customElements.define('quantity-input', QuantityInput);
}
